import React, { useState } from 'react';
import { X, Archive, ArchiveRestore, UserPlus } from 'lucide-react';
import Cookies from 'js-cookie';
import axios from 'axios';
import toast from 'react-hot-toast';

// Zustand Store
import useCanvasStore, { SELECTION_MODE } from '../stores/canvasStore';

import { BACKEND_API_URL } from "../utils/api";
const REACT_APP_BACKEND_API_URL = BACKEND_API_URL;

const MultiSelectToast = ({ selectedItems, selectionMode, hasSelectedItemsAtThisDepth, onAssignClick }) => {
    const clearSelection = useCanvasStore(s => s.clearSelection);
    const updateItemOptimistic = useCanvasStore(s => s.updateItemOptimistic);
    const fetchCanvas = useCanvasStore(s => s.fetchCanvas);
    const currentFolder = useCanvasStore(s => s.currentFolder);
    const archiveStatus = useCanvasStore(s => s.archiveStatus);
    const [isArchiving, setIsArchiving] = useState(false);

    if (selectedItems.length === 0 || !hasSelectedItemsAtThisDepth) {
        return null;
    }

    const folderCount = selectedItems.filter(item => item.type === 'folder' || item.item_type === 'folder').length;
    const linkCount = selectedItems.filter(item => item.type === 'link' || item.item_type === 'link').length;
    const parts = [];
    
    if (folderCount > 0) parts.push(`${folderCount} folder${folderCount > 1 ? 's' : ''}`);
    if (linkCount > 0) parts.push(`${linkCount} link${linkCount > 1 ? 's' : ''}`);

    // Check if any selected items are already archived
    const hasArchivedItems = selectedItems.some(item => item.is_archived);
    const hasUnarchivedItems = selectedItems.some(item => !item.is_archived);

    const handleArchiveClick = async (newArchiveStatus) => {
        setIsArchiving(true);
        const accessToken = Cookies.get('access_token');
        let successCount = 0;
        let errorCount = 0;

        try {
            // Process all selected items
            for (const item of selectedItems) {
                try {
                    // Optimistic update via Zustand
                    if (item.depth) {
                        updateItemOptimistic(item.id, item.depth, { is_archived: newArchiveStatus });
                    }

                    await axios.get(
                        `${REACT_APP_BACKEND_API_URL}/api/item_archive_status_change/${item.id}?status=${newArchiveStatus}`,
                        {
                            headers: {
                                'Authorization': `Token ${accessToken}`,
                                'Content-Type': 'application/json',
                            },
                        }
                    );

                    successCount++;
                } catch (error) {
                    console.error(`Error archiving item ${item.id}:`, error);
                    // Rollback optimistic update on error
                    if (item.depth) {
                        updateItemOptimistic(item.id, item.depth, { is_archived: !newArchiveStatus });
                    }
                    errorCount++;
                }
            }

            // Show results
            if (successCount > 0) {
                toast.success(`${successCount} item(s) ${newArchiveStatus ? 'archived' : 'unarchived'} successfully`);
            }
            if (errorCount > 0) {
                toast.error(`Failed to ${newArchiveStatus ? 'archive' : 'unarchive'} ${errorCount} item(s)`);
            }

            // Clear selection and re-fetch if archive filter is active
            if (successCount > 0) {
                clearSelection();
                // Re-fetch to update view if we're filtering by archive status
                if (archiveStatus !== 'all') {
                    await fetchCanvas(currentFolder?.id);
                }
            }
        } catch (error) {
            toast.error("Error updating archive status");
        } finally {
            setIsArchiving(false);
        }
    };

    return (
        <div className="flex items-center justify-between gap-2 px-2 py-1 bg-primary/10 border-b border-primary/20">
            {/* Left side - selection info */}
            <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-primary">
                    {selectedItems.length} selected
                </span>
                {parts.length > 0 && (
                    <span className="text-[10px] text-primary/70">
                        ({parts.join(', ')})
                    </span>
                )}
                {selectionMode && (
                    <span className="text-[10px] text-primary/80">
                        • {selectionMode === SELECTION_MODE.MOVE ? 'Ready to move' : 'Ready to copy'}
                    </span>
                )}
            </div>
            
            {/* Right side - action buttons */}
            <div className="flex items-center gap-1">
                {selectedItems.length >= 1 && onAssignClick && (
                    <button
                        onClick={onAssignClick}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-primary hover:bg-primary/10 rounded transition-colors"
                    >
                        <UserPlus className="w-3 h-3" />
                        Assign
                    </button>
                )}
                {hasUnarchivedItems && (
                    <button
                        onClick={() => handleArchiveClick(true)}
                        disabled={isArchiving}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-primary hover:bg-primary/10 rounded transition-colors disabled:opacity-50"
                    >
                        <Archive className="w-3 h-3" />
                        Archive
                    </button>
                )}
                {hasArchivedItems && (
                    <button
                        onClick={() => handleArchiveClick(false)}
                        disabled={isArchiving}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-primary hover:bg-primary/10 rounded transition-colors disabled:opacity-50"
                    >
                        <ArchiveRestore className="w-3 h-3" />
                        Unarchive
                    </button>
                )}
                <span className="text-[9px] text-muted-foreground ml-1">
                    ESC
                </span>
                <button 
                    onClick={() => clearSelection()}
                    className="p-0.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
                >
                    <X className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
    );
};

export default MultiSelectToast;
